import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";

      import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    Refresh
} from "@mui/icons-material";

import {
    getPOHOutternCurrentQuarter,
    getPOHOuttern,
    savePOHOuttern,
    updatePOHOuttern
} from "../../../services/pohOutternService";
import PageHeader from "../../../utils/PageHeader";


// ============================================================
// QUARTERS
// ============================================================

const QUARTERS = [
    {
        value: 1,
        label: "JAN.FEB.MAR"
    },
    {
        value: 2,
        label: "APR.MAY.JUN"
    },
    {
        value: 3,
        label: "JUL.AUG.SEP"
    },
    {
        value: 4,
        label: "OCT.NOV.DEC"
    }
];


// ============================================================
// FIXED POH OUTTERN SCREEN
// IMPORTANT:
// These rows are ALWAYS displayed.
// If database data is not available for the selected
// quarter/year, OutTern is automatically shown as 0.
// ============================================================

const FIXED_LAYOUT = [
    {
        allocation: "Normal POH PCV",
        rows: [
            {
                allocation: "06-231",
                dbAllocation: "06.0231.00(POH)",
                type: "Non AC"
            },
            {
                allocation: "06-231",
                dbAllocation: "06.0231.00(POH)",
                type: "AC"
            }
        ]
    },

    {
        allocation: "Spl Repair",
        rows: [
            {
                allocation: "06-241",
                dbAllocation: "06.0241.00",
                type: "Non AC"
            },
            {
                allocation: "06-241",
                dbAllocation: "06.0241.00",
                type: "AC"
            }
        ]
    },

    {
        allocation: "Corr. POH PCV",
        rows: [
            {
                allocation: "06-232",
                dbAllocation: "06.0232.00",
                type: "Non AC"
            },
            {
                allocation: "06-232",
                dbAllocation: "06.0232.00",
                type: "AC"
            }
        ]
    },

    {
        allocation: "LHB",
        rows: [
            {
                allocation: "LHB",
                dbAllocation: "06.0231.00(LHB)",
                type: "Non AC"
            },
            {
                allocation: "LHB",
                dbAllocation: "06.0231.00(LHB)",
                type: "AC"
            }
        ]
    },

    {
        allocation: "Normal POH OCV",
        rows: [
            {
                allocation: "06-233",
                dbAllocation: "06.0233.00",
                type: "Non AC"
            },
            {
                allocation: "06-233",
                dbAllocation: "06.0233.00",
                type: "AC"
            }
        ]
    },

    {
        allocation: "06-550 Non AC",
        rows: [
            {
                allocation: "06-550",
                dbAllocation: "06.0550.00",
                type: "Non AC",
                calculatedTotal: "NON_AC"
            }
        ]
    },

    {
        allocation: "Corr. POH OCV",
        rows: [
            {
                allocation: "06-234",
                dbAllocation: "06.0234.00",
                type: "Non AC"
            },
            {
                allocation: "06-234",
                dbAllocation: "06.0234.00",
                type: "AC"
            }
        ]
    },

    {
        allocation: "06-560 AC",
        rows: [
            {
                allocation: "06-560",
                dbAllocation: "06.0560.00",
                type: "AC",
                calculatedTotal: "AC"
            }
        ]
    }
];


// ============================================================
// CREATE EMPTY SCREEN
// ============================================================

const createEmptyRows = () => {

    return FIXED_LAYOUT.flatMap(group =>
        group.rows.map(item => ({
            allocation: item.allocation,
            dbAllocation: item.dbAllocation,
            type: item.type,
            calculatedTotal: item.calculatedTotal || null,
            outTern: 0,
            outTernValue: 0,
            exists: false,
            editing: false
        }))
    );

};


// ============================================================
// COMPONENT
// ============================================================

export default function POHOuttern() {

    // --------------------------------------------------------
    // REFS
    // --------------------------------------------------------

    const inputRefs = useRef([]);


    // --------------------------------------------------------
    // STATE
    // --------------------------------------------------------

    const [quarter, setQuarter] =
        useState(1);

    const [year, setYear] =
        useState(new Date().getFullYear());

    const [rows, setRows] =
        useState(createEmptyRows());

    const [loading, setLoading] =
        useState(false);

    const [saving, setSaving] =
        useState(false);


    // ========================================================
    // QUARTER NAME
    // ========================================================

    const quarterName =
        useMemo(() => {

            return (
                QUARTERS.find(
                    q => q.value === Number(quarter)
                )?.label || ""
            );

        }, [quarter]);


    // ========================================================
    // LOAD CURRENT QUARTER
    // ========================================================

    useEffect(() => {

        loadCurrentQuarter();

    }, []);


    // ========================================================
    // LOAD DATA WHEN QUARTER/YEAR CHANGES
    // ========================================================

    useEffect(() => {

        const numericYear =
            Number(year);

        if (
            numericYear >= 2000 &&
            numericYear <= 2100 &&
            [1, 2, 3, 4].includes(
                Number(quarter)
            )
        ) {

            loadData(
                Number(quarter),
                numericYear
            );

        }

    }, [quarter, year]);


    // ========================================================
    // CURRENT QUARTER
    // ========================================================

    const loadCurrentQuarter = async () => {

        try {

            const response =
                await getPOHOutternCurrentQuarter();

            if (
                response.data?.success
            ) {

                const data =
                    response.data.data;

                setQuarter(
                    Number(data.quarter)
                );

                setYear(
                    Number(data.year)
                );

            }

        } catch (error) {

            console.error(error);

        }

    };


    // ========================================================
    // LOAD DATA
    // ========================================================

    const loadData = async (
        selectedQuarter,
        selectedYear
    ) => {

        try {

            setLoading(true);

            // ------------------------------------------------
            // ALWAYS START WITH THE COMPLETE FIXED SCREEN.
            // This is the important change.
            // ------------------------------------------------

            const emptyScreen =
                createEmptyRows();

            const response =
                await getPOHOuttern(
                    selectedQuarter,
                    selectedYear
                );

            const data =
                response.data?.data || [];


            // ------------------------------------------------
            // MERGE DATABASE DATA INTO THE FIXED SCREEN.
            //
            // If a row does not exist in DB:
            //     outTern = 0
            //     exists = false
            //
            // If a row exists:
            //     use DB value
            //     exists = true
            // ------------------------------------------------

            const formatted =
                emptyScreen.map(row => {

                    const found =
                        data.find(item => {

                            const dbAllocation =
                                String(
                                    item["POH Allocation"] ?? ""
                                ).trim();

                            const dbType =
                                String(
                                    item["POH Type"] ?? ""
                                ).trim();

                            // IMPORTANT:
                            // Database allocations are not the same as
                            // the labels displayed on the screen.
                            //
                            // Example:
                            // Screen : 06-231
                            // DB     : 06.0231.00(POH)
                            //
                            // Therefore match using dbAllocation.

                            return (
                                dbAllocation.toLowerCase() ===
                                    String(
                                        row.dbAllocation
                                    ).trim().toLowerCase()
                                &&
                                dbType.toLowerCase() ===
                                    String(
                                        row.type
                                    ).trim().toLowerCase()
                            );

                        });


                    // 06-550 and 06-560 are calculated totals.
                    // Do NOT take their stored database value.
                    if (row.calculatedTotal) {

                        return {
                            ...row,
                            outTern: 0,
                            outTernValue: 0,
                            exists: Boolean(found),
                            editing: false
                        };

                    }


                    if (!found) {

                        return {
                            ...row,
                            outTern: 0,
                            outTernValue: 0,
                            exists: false,
                            editing: false
                        };

                    }


                    return {
                        ...row,

                        outTern:
                            Number(
                                found["OutTern"] ?? 0
                            ),

                        outTernValue:
                            Number(
                                found["OutTernValue"] ?? 0
                            ),

                        exists: true,
                        editing: false
                    };

                });


            setRows(formatted);

        } catch (error) {

            console.error(error);

            // Even when the API fails, keep the complete
            // screen visible with zero values.

            setRows(
                createEmptyRows()
            );

            alert(
                error.response?.data?.message ||
                "Unable to load POH Outtern data"
            );

        } finally {

            setLoading(false);

        }

    };


    // ========================================================
    // UPDATE ROW
    // ========================================================

    const handleChange = (
        index,
        value
    ) => {

        // Allow only numbers / decimal values.
        if (
            value !== "" &&
            (
                Number(value) < 0 ||
                !/^\d*\.?\d*$/.test(value)
            )
        ) {

            return;

        }


        setRows(prev => {

            const copy = [...prev];

            copy[index] = {

                ...copy[index],

                outTern:
                    value === ""
                        ? ""
                        : value

            };

            return copy;

        });

    };


    // ========================================================
    // ENTER KEY NAVIGATION
    // ========================================================

    const handleKeyDown = (
        event,
        index
    ) => {

        if (
            event.key !== "Enter"
        ) {

            return;

        }

        event.preventDefault();

        const nextIndex =
            index + 1;

        if (
            inputRefs.current[nextIndex]
        ) {

            inputRefs.current[nextIndex].focus();

            inputRefs.current[nextIndex].select();

        }

    };


    // ========================================================
    // CALCULATED TOTALS
    //
    // 06-550 = total of ALL editable Non AC rows
    // 06-560 = total of ALL editable AC rows
    //
    // Calculated rows are excluded from their own totals.
    // ========================================================

    const getCalculatedTotal = (totalType) => {

        return rows.reduce(
            (sum, row) => {

                if (row.calculatedTotal) {
                    return sum;
                }

                const rowType =
                    String(row.type || "")
                        .trim()
                        .toLowerCase();

                if (
                    totalType === "NON_AC" &&
                    rowType === "non ac"
                ) {
                    return sum + Number(row.outTern || 0);
                }

                if (
                    totalType === "AC" &&
                    rowType === "ac"
                ) {
                    return sum + Number(row.outTern || 0);
                }

                return sum;

            },
            0
        );

    };


    // ========================================================
    // CALCULATE PERCENTAGE
    // ========================================================

    const getPercentage = (
        allocation,
        index
    ) => {

        const sameAllocation =
            rows.filter(
                row =>
                    row.allocation === allocation
            );


        const total =
            sameAllocation.reduce(
                (
                    sum,
                    row
                ) =>
                    sum +
                    Number(
                        row.outTern || 0
                    ),
                0
            );


        const current =
            Number(
                rows[index]?.outTern || 0
            );


        if (!total) {

            return 0;

        }


        return (
            current / total
        ) * 100;

    };


    // ========================================================
    // SAVE / UPDATE
    // ========================================================

   const handleSave = async () => {

    // --------------------------------------------------------
    // CONFIRM BEFORE SAVE
    // --------------------------------------------------------

    const confirmed = window.confirm(
        `Are you sure you want to save POH Outtern data?\n\n` +
        `Quarter: ${quarterName}\n` +
        `Year: ${year}`
    );

    if (!confirmed) {
        return;
    }

    try {

        setSaving(true);

        // ----------------------------------------------------
        // EXISTING SAVE CODE
        // ----------------------------------------------------

        for (const row of rows) {

            if (row.calculatedTotal) {
                continue;
            }

            const count = Number(row.outTern);

            if (
                !Number.isFinite(count) ||
                count < 0
            ) {
                alert(
                    `Invalid OutTern for ${row.allocation} - ${row.type}`
                );

                return;
            }

            const payload = {
                pohAllocation: row.dbAllocation,
                pohType: row.type,
                outTern: count,
                quarter: Number(quarter),
                year: Number(year)
            };

            if (row.exists) {

                await updatePOHOuttern(payload);

            } else {

                await savePOHOuttern(payload);

            }
        }

        alert(
            "POH Outtern saved successfully"
        );

        await loadData(
            quarter,
            year
        );

    } catch (error) {

        console.error(error);

        alert(
            error.response?.data?.message ||
            "Unable to save POH Outtern"
        );

    } finally {

        setSaving(false);
    }
};


    // ========================================================
    // GROUP DATA FOR DISPLAY
    // ========================================================

    const displayGroups =
        useMemo(() => {

            let rowIndex = 0;

            return FIXED_LAYOUT.map(
                group => {

                    const groupRows =
                        group.rows.map(
                            item => {

                                const row =
                                    rows[rowIndex] ||
                                    {
                                        allocation:
                                            item.allocation,
                                        dbAllocation:
                                            item.dbAllocation,
                                        type:
                                            item.type,
                                        calculatedTotal:
                                            item.calculatedTotal || null,
                                        outTern: 0,
                                        exists: false
                                    };

                                const result = {
                                    ...row,
                                    index: rowIndex
                                };

                                rowIndex++;

                                return result;

                            }
                        );


                    return {
                        ...group,
                        rows: groupRows
                    };

                }
            );

        }, [rows]);


    // ========================================================
    // RENDER
    // ========================================================

    return (

         <Box
      sx={{
        p: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >

            {/* =================================================
                HEADER
            ================================================= */}

            <PageHeader
        title=" POH Outtern Data for the Quarter"
        icon={AccessTimeIcon}
       // statusLabel={'Records: ' + totalRows}
       //  statusValue="CSV File"
        // statusChip={{
        //   label: loading
        //     ? "Loading..."
        //     : totalRows > 0
        //       ? "Data Found"
        //       : "No Data",
        //   color: loading ? "warning" : totalRows > 0 ? "primary" : "default",
        // }}
        // showRefresh
        // onRefresh={handleRefresh}
        // refreshing={loading}
      />






            {/* =================================================
                FILTER
            ================================================= */}

            <Card
                elevation={2}
                sx={{
                    mb: 2,
                    borderRadius: 2
                }}
            >

                <CardContent>

                   <Box
    sx={{
        display: "grid",
        gridTemplateColumns: {
            xs: "1fr",       // Mobile: 1 column
            md: "repeat(2, minmax(0, 1fr))", // PC: 2 columns
        },
        gap: 2,
        width: "100%",
    }}
>

                        <div
                            item
                            xs={12}
                            sm={6}
                            md={4}
                        >

                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Select Quarter"
                                value={String(quarter)}
                                onChange={e => {
                                    const selectedQuarter =
                                        Number(e.target.value);

                                    if (
                                        [1, 2, 3, 4].includes(
                                            selectedQuarter
                                        )
                                    ) {
                                        setQuarter(
                                            selectedQuarter
                                        );
                                    }
                                }}
                                SelectProps={{
                                    displayEmpty: false
                                }}
                            >

                                {QUARTERS.map(q => (

                                    <MenuItem
                                        key={q.value}
                                        value={String(q.value)}
                                    >
                                        {q.label}
                                    </MenuItem>

                                ))}

                            </TextField>

                        </div>


                        <div
                            item
                            xs={12}
                            sm={6}
                            md={3}
                        >

                            <TextField
                                fullWidth
                                size="small"
                                label="Year (YYYY)"
                                type="number"
                                value={year}
                                onChange={e =>
                                    setYear(
                                        e.target.value
                                    )
                                }
                                inputProps={{
                                    min: 2000,
                                    max: 2100
                                }}
                            />

                        </div>


                        <div
                            item
                            xs={12}
                            md={5}
                            sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: {
                                    xs: "stretch",
                                    md: "flex-end"
                                }
                            }}
                        >

                            <Button
                                variant="outlined"
                                startIcon={
                                    <Refresh />
                                }
                                onClick={() =>
                                    loadData(
                                        quarter,
                                        year
                                    )
                                }
                                disabled={
                                    loading ||
                                    saving
                                }
                            >
                                Refresh
                            </Button>

                        </div>

                    </Box>

                </CardContent>

            </Card>


            {/* =================================================
                DATA
                ALWAYS SHOW ALL FIXED GROUPS.
            ================================================= */}

            {loading ? (

                <Card
                    sx={{
                        p: 4,
                        textAlign: "center"
                    }}
                >

                    <Typography>
                        Loading...
                    </Typography>

                </Card>

            ) : (

               <Box
    sx={{
        display: "grid",
        gridTemplateColumns: {
            xs: "1fr",       // Mobile: 1 column
            md: "repeat(2, minmax(0, 1fr))", // PC: 2 columns
        },
        gap: 2,
        width: "100%",
    }}
>
    {displayGroups.map(group => (

        <Box
            key={group.allocation}
            sx={{
                width: "100%",
                minWidth: 0,
            }}
        >

                                <Card
                                    elevation={2}
                                    sx={{
                                        height: "100%",
                                        borderRadius: 2
                                    }}
                                >

                                    <CardContent>

                                        {/* GROUP HEADER */}

                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: "#555",
                                                mb: 1
                                            }}
                                        >
                                            {group.allocation}
                                        </Typography>


                                        <Divider
                                            sx={{
                                                mb: 1.5
                                            }}
                                        />


                                        {/* ROWS */}

                                        {group.rows.map(
                                            row => {

                                                const percentage =
                                                    row.calculatedTotal
                                                        ? (
                                                            getCalculatedTotal(
                                                                row.calculatedTotal
                                                            ) > 0
                                                                ? 100
                                                                : 0
                                                        )
                                                        : getPercentage(
                                                            row.allocation,
                                                            row.index
                                                        );


                                                return (

                                                    <Box
                                                        key={`${group.allocation}-${row.allocation}-${row.type}`}
                                                        sx={{
                                                            display: "grid",
                                                            gridTemplateColumns: {
                                                                xs: "1fr",
                                                                sm: "minmax(130px, 1fr) 110px 100px"
                                                            },
                                                            gap: 1,
                                                            alignItems: "center",
                                                            mb: 1.25
                                                        }}
                                                    >

                                                        {/* TYPE */}

                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: "#d00000",
                                                                fontSize: {
                                                                    xs: "0.9rem",
                                                                    sm: "0.95rem"
                                                                }
                                                            }}
                                                        >
                                                            {row.allocation}
                                                            {" "}
                                                            {row.type}
                                                        </Typography>


                                                        {/* OUTTERN */}

                                                        <TextField
                                                            inputRef={element => {
                                                                inputRefs.current[row.index] =
                                                                    element;
                                                            }}
                                                            size="small"
                                                            fullWidth
                                                            type="number"
                                                            label="OutTern"
                                                            value={
                                                                row.calculatedTotal === "NON_AC"
                                                                    ? getCalculatedTotal("NON_AC")
                                                                    : row.calculatedTotal === "AC"
                                                                        ? getCalculatedTotal("AC")
                                                                        : row.outTern
                                                            }
                                                            onChange={e => {
                                                                if (
                                                                    !row.calculatedTotal
                                                                ) {
                                                                    handleChange(
                                                                        row.index,
                                                                        e.target.value
                                                                    );
                                                                }
                                                            }}
                                                            onKeyDown={e => {
                                                                if (
                                                                    !row.calculatedTotal
                                                                ) {
                                                                    handleKeyDown(
                                                                        e,
                                                                        row.index
                                                                    );
                                                                }
                                                            }}
                                                            disabled={
                                                                Boolean(
                                                                    row.calculatedTotal
                                                                )
                                                            }
                                                            InputProps={{
                                                                readOnly:
                                                                    Boolean(
                                                                        row.calculatedTotal
                                                                    )
                                                            }}
                                                            inputProps={{
                                                                min: 0,
                                                                step: "any"
                                                            }}
                                                        />


                                                        {/* PERCENTAGE */}

                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            label="%"
                                                            disabled
                                                            value={
                                                                percentage.toFixed(5)
                                                            }
                                                            InputProps={{
                                                                readOnly: true
                                                            }}
                                                        />

                                                    </Box>

                                                );

                                            }
                                        )}

                                    </CardContent>

                                </Card>

                            </Box>

                        )
                    )}

                </Box>

            )}


            {/* =================================================
                SAVE
            ================================================= */}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 3
                }}
            >

                <Button
                    variant="contained"
                    color="success"
                    size="large"
                    disabled={
                        saving ||
                        loading ||
                        rows.length === 0
                    }
                    onClick={handleSave}
                    sx={{
                        minWidth: {
                            xs: "100%",
                            sm: 220
                        },
                        fontWeight: 700
                    }}
                >

                    {saving
                        ? "Saving..."
                        : "Save"
                    }

                </Button>

            </Box>

        </Box>

    );

}
